import { useEffect, useRef } from 'react';

/**
 * Hook that handles click-outside detection for Material-UI dropdowns
 * 
 * This hook detects clicks outside Material-UI Select/Menu dropdowns and closes them.
 * It works around the issue where Material-UI's built-in click-away detection
 * is interfered with by other capture-phase event listeners (e.g., Google Analytics).
 * 
 * @param openStates - Array of objects with `isOpen` boolean and `setOpen` setter function
 * 
 * @example
 * ```tsx
 * const [statusOpen, setStatusOpen] = useState(false);
 * const [modeOpen, setModeOpen] = useState(false);
 * 
 * useMaterialUIDropdownClose([
 *   { isOpen: statusOpen, setOpen: setStatusOpen },
 *   { isOpen: modeOpen, setOpen: setModeOpen }
 * ]);
 * ```
 */
export const useMaterialUIDropdownClose = (
  openStates: Array<{ isOpen: boolean; setOpen: (open: boolean) => void }>
): void => {
  // Callers often pass a fresh array literal each render. Depend on open flags only
  // so we do not re-bind document listeners (and risk update churn) every render.
  const openFlagsKey = openStates.map((state) => (state.isOpen ? '1' : '0')).join(',');
  const openStatesRef = useRef(openStates);
  openStatesRef.current = openStates;

  useEffect(() => {
    const states = openStatesRef.current;
    // Check if any dropdown is open
    const hasOpenDropdown = states.some(state => state.isOpen);
    if (!hasOpenDropdown) {
      return;
    }

    const handleDocumentClick = (e: MouseEvent) => {
      // Text nodes (and other non-Elements) can be event targets; they have no `.closest`.
      const raw = e.target;
      if (!(raw instanceof Node)) return;
      const target = raw instanceof Element ? raw : raw.parentElement;
      if (!target) return;

      // Only ignore clicks on the Select / menu trigger that currently owns the open menu.
      // Do NOT skip all `.MuiInputBase-root` — that blocks DatePicker/TextField outside
      // clicks from closing the dropdown (common on event create wizard).
      const openSelect = document.querySelector(
        '.MuiSelect-root[aria-expanded="true"], .MuiSelect-select[aria-expanded="true"]',
      );
      if (openSelect) {
        const selectRoot =
          openSelect.closest('.MuiSelect-root, .MuiInputBase-root') ?? openSelect;
        if (selectRoot.contains(target)) {
          return;
        }
      }

      const openMenuTrigger = document.querySelector(
        '[aria-haspopup="menu"][aria-expanded="true"], [aria-haspopup="true"][aria-expanded="true"]',
      );
      if (openMenuTrigger && openMenuTrigger.contains(target)) {
        // Let the trigger toggle; don't race-close on the same mousedown.
        return;
      }

      // CRITICAL: Skip clicks on AdSense elements to avoid interfering with ad functionality
      // This prevents dropdown handlers from blocking AdSense click detection
      if (target.closest('.adsbygoogle, [data-ad-client], [data-ad-slot], [data-ad-format], ins.adsbygoogle, [id*="google_ads"], [id*="goog-"], [id*="aswift_"]') !== null) {
        return; // Don't interfere with AdSense clicks
      }

      // Find the open menu / listbox surface (Select uses listbox; Menu uses menu).
      // Include bare Popover paper so macros/filter popovers still hit-test when
      // role="menu" is missing or not yet applied.
      const menu =
        document.querySelector('.MuiMenu-root [role="menu"]') ||
        document.querySelector('.MuiPopover-root [role="menu"]') ||
        document.querySelector('.MuiPopover-root [role="listbox"]') ||
        document.querySelector('.MuiMenu-paper') ||
        document.querySelector('.MuiPopover-root .MuiPopover-paper');
      const currentStates = openStatesRef.current;
      if (!menu) {
        // Portal lag (common on iOS/WebKit): menu is not in the DOM yet.
        // Force-closing here races open→close→open and can trip React #185
        // (maximum update depth) with controlled MUI Selects.
        return;
      }

      const rect = menu.getBoundingClientRect();
      const clickX = e.clientX;
      const clickY = e.clientY;

      // Check if click is outside the menu
      if (
        clickX < rect.left ||
        clickX > rect.right ||
        clickY < rect.top ||
        clickY > rect.bottom
      ) {
        currentStates.forEach((state) => {
          if (state.isOpen) {
            state.setOpen(false);
          }
        });
      }
    };

    // Use capture-phase mousedown so we still close when bubble-phase
    // ClickAwayListener is interfered with (e.g. analytics capture listeners).
    document.addEventListener('mousedown', handleDocumentClick, true);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick, true);
    };
  }, [openFlagsKey]);
};

export default useMaterialUIDropdownClose;
